<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <div v-if="!isEdit && !item" class="columns is-tablet">
            <form-field
                class="column"
                label="Vare"
                :error="error.item_id"
            >
                <InputSelectSearch
                    :options="mappedItems"
                    v-model="selectedItem"
                    :error="error.item_id"
                    @search="search"
                    @input="onItemInput"
                />
            </form-field>
            <br />
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Serienummer"
                :error="error.serial"
            >
                <input-text
                    v-model="form.serial"
                    :error="error.serial"
                    :disabled="form.is_ghost || serialLocked"
                />
            </form-field>

            <form-field
                class="column"
                label="Lokation"
                :error="error.location_id"
                inner-container-class="is-fullwidth"
            >
                <InputSelectSearch
                    v-model="location"
                    :options="locations"
                    label="full_name"
                    option-value-prop="id"
                    placeholder="Vælg..."
                    :filter="filterLocations"
                />
            </form-field>
            <form-field
                class="column"
                label="Status"
                :error="error.status_id"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="serialStatuses"
                    v-model="form.status_id"
                    option-value-prop="id"
                    option-title-prop="name"
                    :error="error.status_id"
                    :disabled="!editableStatus"
                    @input="onStatusSelected"
                />
            </form-field>
        </div>

        <div class="columns is-tablet" v-if="showGhostOption">
            <label class="checkbox is-ghost-margin">
                <input type="checkbox" v-model="form.is_ghost" @change="isGhostChanged" :disabled="serialLocked">
                Intet serienummer
            </label>
        </div>

        <div class="columns is-tablet">
            <div class="column is-full">
                <form-field label="Priser" class="mb-2">
                    <vue-select
                        class="vue-select-bulma"
                        multiple
                        v-model="form.prices"
                        :options="priceOptions"
                    >
                        <span slot="no-options">Ingen priser fundet</span>
                    </vue-select>
                </form-field>
            </div>
        </div>

        <div class="columns mb-2">
            <form-field
                class="column"
                label="Note"
                :error="error.note"
            >
                <InputTextarea v-model="form.note" rows="4"/>
            </form-field>
        </div>

        <template v-slot:footer class="has-space-between">
            <div class="footer-spacer">
                <div>
                    <button class="button is-danger is-flex-left" @click="reset">Nulstil</button>
                    <button class="button is-default" @click="$emit('closed', true)">Annuller</button>
                    <button class="button is-primary" @click="submit">{{ saveButtonText }}</button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">Slet</button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from '@elems/modal'
import {FormField, InputText, InputCheckbox, InputSelect, InputTextarea, InputSelectSearch} from '@elems/form'
import Prices from "@elems/prices/Index";
import debounce from 'lodash/debounce'
import VueSelect from 'vue-select'
import serialStatuses from '../../../store/serialStatuses';

export default {
    components: {
        InputTextarea,
        Modal,
        FormField,
        InputText,
        InputSelect,
        InputCheckbox,
        Prices,
        InputSelectSearch,
        VueSelect
    },
    props: {
        item: {
            type: Object,
            required: false,
        },
        active: {
            type: Boolean,
            required: true,
        },
        serial: {
            type: Object,
            required: false,
        },
        isAdmin: {
            type: Boolean,
            required: true
        },
        showAdminFields: {
            type: Boolean,
            required: true
        },
        editableStatus: {
            type: Boolean,
            default: true,
        },
        defaultStatusKey: {
            type: String,
            required: false,
        }
    },
    data() {
        return {
            form: {
                serial: null,
                location_id: null,
                status_id: null,
                note: '',
                is_ghost: false,
                item_id: null,
                prices: [],
            },
            location: null,
            error: [],
            locations: [],
            serialStatuses: [],
            items: [],
            serialLocked: false,
            selectedInputItem: null,
            priceOptions: [],
        }
    },
    computed: {
        showGhostOption() {
            if (this.isEdit) {
                return this.serial.is_ghost;
            }

            return true;
        },
        selectedItem: {
            get() {
                const item = this.items.find(item => item.id === this.form.item_id)
                return item ? {id: item.id, label: item.model + ' (' + item.id + ')'} : null
            },
            set(value) {
                this.form.item_id = value ? value.id : null;
            }
        },
        isEdit() {
            return this.serial !== null
        },
        saveButtonText() {
            return this.isEdit ? 'Gem' : 'Opret'
        },
        mappedItems() {
            return this.items.map(item => ({id: item.id, label: item.model + ' (' + item.id + ')'}));
        },
        selectedStatus() {
            return this.form.status_id ? this.serialStatuses.find(status => status.id === parseInt(this.form.status_id)) : null;
        },
        locationDisabled() {
            return this.selectedStatus ? this.selectedStatus.no_location : false;
        }
    },
    methods: {
        filterLocations(options, search) {
            const terms = search.toLocaleLowerCase().split(' ');
            return options.filter(option => {
                return terms.every(term => {
                    return option.full_name.toLocaleLowerCase().indexOf(term) > -1
                });
            });
        },
        onStatusSelected() {
            if (this.locationDisabled) {
                this.form.location_id = null;
            }
        },
        onItemInput(item) {
            this.priceOptions = []
            this.form.prices = []

            if (item) {
                window.axios.get('/api/items/' + item.id)
                    .then((response) => {
                        this.setPriceOptions(response.data.prices, true)
                    })
                    .catch(error => {
                        console.error(error)
                    });
            }
        },
        setPriceOptions(prices, selectOptions) {
            this.priceOptions = []
            prices.forEach(price => {
                const option = {
                    id: price.id,
                    label: price.manufacturer_price_type.name
                }
                this.priceOptions.push(option)
                if (selectOptions) {
                    this.form.prices.push(option)
                }
            })
        },
        search: debounce(function (query) {
            if (query)
                this.fetchItems(query);
        }, 200),
        fetchItems(query) {
            let params = {q: query};
            params['type-key'] = 'with_serial';
            window.axios.get(`/api/items`, { params })
                .then((data) => {
                    this.items = data.data.data;
                })
                .catch(error => {
                    console.error(error)
                });
        },
        title() {
            return this.isEdit
                ? 'Rediger serienummer'
                : 'Opret serienummer'
        },
        submit() {
            return this.isEdit ? this.update() : this.create()
        },
        create() {
            this.error = []
            const itemId = this.item ? this.item.id : this.form.item_id

            if (itemId) {
                if (this.form.serial) {
                    if (!confirm('Serienummer kan ikke ændres efter oprettelse, tryk på OK for at fortsætte.')) {
                        return
                    }
                }

                window.axios.post(`/api/items/${itemId}/serials`, this.form)
                    .then((response) => {
                        this.$emit('closed')
                        this.$emit('updated', response.data)
                        this.$store.dispatch('setSuccess', 'Serienummeret blev oprettet')
                        this.reset()
                    })
                    .catch(error => {
                        if (error.response && error.response.status === 422) {
                            this.error = error.response.data.errors
                        }
                    });
            }
            else {
                this.error = {item_id: ['Vare skal udfyldes']}
            }
        },
        update() {
            if (this.isEdit && this.form.serial && !this.serialLocked) {
                if (!confirm('Serienummer feltet låses, tryk på OK for at fortsætte.')) {
                    return
                }
            }

            window.axios.put('/api/serials/' + this.serial.id, this.form)
                .then(() => {
                    this.$emit('closed')
                    this.$emit('updated')
                    this.$store.dispatch('setSuccess', 'Serienummeret blev opdateret')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                })
        },
        fetchLocations() {
            window.axios.get('/api/locations')
                .then((data) => {
                    this.locations = data.data;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        fetchSerialStatuses(cb = null) {
            window.axios.get('/api/serial-statuses')
                .then((data) => {
                    this.serialStatuses = data.data;
                    if (cb) {
                        cb(data.data);
                    }
                })
                .catch(error => {
                    console.error(error);
                })
        },
        reset() {
            if (this.isEdit) {
                this.form = {
                    serial: this.serial.serial,
                    location_id: this.serial.location ? this.serial.location.id : null,
                    status_id: this.serial.status.id,
                    note: this.serial.note,
                    is_ghost: this.serial.is_ghost,
                    prices: []
                }

                this.location = this.serial.location

                this.serial.prices.forEach(price => this.form.prices.push({
                    id: price.id, 
                    label: price.manufacturer_price_type.name
                }))
            } else {
                this.form = {
                    serial: '',
                    location_id: null,
                    status_id: this.editableStatus ? null : this.form.status_id,
                    note: '',
                    is_ghost: false,
                    item_id: null,
                    prices: [],
                }

                this.location = null

                if (!this.item) {
                    this.priceOptions = []
                }

                this.priceOptions.forEach(price => this.form.prices.push({id: price.id, label: price.label}))
            }
            this.error = []

            this.serialLocked = this.isEdit && this.form.serial
        },
        remove() {
            if (!confirm('Er du sikker på du vil slette ' + this.form.serial + '?')) {
                return
            }

            window.axios.delete('/api/serials/' + this.serial.id)
                .then(() => {
                    this.$emit('closed')
                    this.$emit('updated')
                    this.$store.dispatch('setSuccess', 'Serienummeret blev slettet')
                })
                .catch(error => {
                    console.error(error)
                    this.$store.dispatch('setError', 'Serienummeret kunne ikke slettes')
                })
        },
        isGhostChanged() {
            if (this.form.is_ghost)
                this.form.serial = ''
        },
        setDefaultStatus(serialStatuses) {
            if (this.defaultStatusKey) {
                const status = serialStatuses.find(s => s.key === this.defaultStatusKey);
                this.form.status_id = status.id;
            }
        }
    },
    created() {
        this.fetchLocations();
        this.fetchSerialStatuses(this.setDefaultStatus);

        if (this.isEdit && !this.item) {
            window.axios.get('/api/items/' + this.serial.item.id)
                .then((response) => {
                    this.setPriceOptions(response.data.prices, false)
                })
                .catch(error => {
                    console.error(error)
                });
        }

        if (this.item) {
            this.setPriceOptions(this.item.prices, true)
            this.form.item_id = this.item.id;
        }

        if (this.serial) {
            this.reset()
        }
    },
    watch: {
        serial() {
            this.reset()
        },
        item() {
            if (this.item) {
                this.setPriceOptions(this.item.prices, false)
            }
        },
        location() {
            this.form.location_id = this.location ? this.location.id : null;
        }
    },
}
</script>

<style lang="scss" scoped>
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
}
.customer-details {
    h4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #bbb;
        margin-bottom: 25px;
        cursor: pointer;
        i {
            transition: transform .3s ease-in-out;
        }
    }
    .column {
        display: none;
    }
    &.open {
        h4 i {
            transform: rotate(.5turn);
        }
        .column {
            display: block;
        }
    }
}
.column {
    padding: 0 0.75rem;
}
.is-ghost-margin {
    margin: 0 0 0.5rem 0.75rem;
}
</style>

